/**
 * Receivables Invoice Link Repair -- Phase 2 (gated write).
 *
 * Companion to the read-only Receivables Invoice Link Census card: for each
 * "Unpaid" booking row with candidate unlinked Pennylane invoices, the
 * operator picks ONE candidate via radio button and clicks Link. That
 * writes the missing "{platform}:{booking_id}" external_reference onto
 * exactly that one invoice -- never auto-linked on the heuristic score.
 * After a successful link, the existing exact receivables dedup join
 * (services/receivables_dedup.py, unchanged) picks the pair up on the very
 * next read, so the booking drops out of the next Preview.
 *
 * Same Preview-then-approve shape as StatementOrphanRepointPanel's
 * CandidatePicker.
 */

import { useState } from 'react';
import { Loader2, Search, Link2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { receivablesInvoiceLinkAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function BookingLinkPicker({ booking, onLink, linking }) {
  var [selected, setSelected] = useState(null);
  var candidates = booking.candidates || [];
  var rowKey = booking.platform + ':' + booking.booking_id;
  var isLinking = linking === rowKey;

  return (
    <div className="border border-amber-300 dark:border-amber-800 rounded p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 text-xs">
        <p className="font-mono text-muted-foreground">
          {booking.entity_name} -- {booking.platform}:{booking.booking_id}
        </p>
        <span>{booking.guest_name || '(no guest name)'} -- {booking.amount} {booking.currency}</span>
      </div>

      <div className="space-y-1">
        {candidates.map(function (c) {
          var inputId = rowKey + '__' + c.invoice_id;
          return (
            <label key={c.invoice_id} htmlFor={inputId} className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="radio"
                id={inputId}
                name={'candidate-' + rowKey}
                value={c.invoice_id}
                checked={selected === c.invoice_id}
                onChange={() => setSelected(c.invoice_id)}
              />
              <span className="font-mono">{c.invoice_id}</span>
              <span>{c.supplier_client_name || '(no counterparty name)'}</span>
              <span className="text-muted-foreground">{c.amount} {c.currency}</span>
              <span className="text-muted-foreground">{c.issue_date || '--'}</span>
              <span className={c.amount_within_tolerance ? 'text-green-600' : 'text-muted-foreground'}>
                name match {Math.round((c.name_similarity || 0) * 100)}%
              </span>
            </label>
          );
        })}
      </div>

      <Button
        size="sm"
        variant="outline"
        disabled={!selected || isLinking}
        onClick={() => onLink(selected, booking.platform, booking.booking_id, rowKey)}
      >
        {isLinking ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : null}
        Link
      </Button>
    </div>
  );
}

export function ReceivablesInvoiceLinkRepairPanel() {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);
  var [linkingRowKey, setLinkingRowKey] = useState(null);

  var loadPreview = async function () {
    setLoading(true);
    try {
      var res = await receivablesInvoiceLinkAPI.preview();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var handleLink = async function (invoiceId, platform, bookingId, rowKey) {
    setLinkingRowKey(rowKey);
    try {
      var res = await receivablesInvoiceLinkAPI.resolve(invoiceId, platform, bookingId);
      if (res.data && res.data.linked) {
        toast.success('Linked ' + invoiceId + ' to ' + platform + ':' + bookingId + '.');
      } else {
        toast.error('Not linked: ' + ((res.data && res.data.reason) || 'unknown reason') + '. Re-run Preview.');
      }
      await loadPreview();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLinkingRowKey(null);
    }
  };

  var bookingsWithCandidates = data ? data.bookings_with_candidates || [] : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Link2 className="h-4 w-4" />
          Receivables Invoice Link Repair
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Write path for the Receivables Invoice Link Census above. Pick one
          candidate invoice per booking and click Link -- this writes the
          missing external_reference onto that one invoice only. No
          auto-linking: every write is an explicit, row-by-row approval.
        </p>

        <Button onClick={loadPreview} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Preview
        </Button>

        {data && (
          <div className="space-y-3">
            <p className="text-sm">{data.verdict}</p>

            {bookingsWithCandidates.length === 0 ? (
              <p className="text-xs text-muted-foreground">No booking rows with candidate invoices to link.</p>
            ) : (
              <div className="space-y-2">
                {bookingsWithCandidates.map(function (b) {
                  return (
                    <BookingLinkPicker
                      key={b.platform + ':' + b.booking_id}
                      booking={b}
                      onLink={handleLink}
                      linking={linkingRowKey}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
